import {Form, FormField, FormSubmitHandler, Separator, showNotification} from 'platform/components';
import {Grid, Space} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useInviteUserMutation} from '@dms/api/core';
import {useSetOtherUserSettingsItemMutation} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {UserBranches} from '../UserDetail/components/UserBranches';
import {UserRoles} from '../UserDetail/components/UserRoles';
import {FormValues, getSchema} from '../UserDetail/types/FormValues';
import {UserInfo} from './components/UserInfo';

export function InviteUserDetail() {
  const navigate = useNavigate();

  const [inviteUser] = useInviteUserMutation();
  const [saveOtherUserSetting] = useSetOtherUserSettingsItemMutation();

  const updateOtherSettings = (data: FormValues) => async (user: {id: string}) => {
    const userId = user.id;

    if (isNotNilOrEmpty(data.customId)) {
      await saveOtherUserSetting({
        userId,
        settingKey: 'custom_id',
        body: {
          value: data.customId ?? '',
        },
      })
        .unwrap()
        .catch(handleApiError);
    }

    if (isNotNilOrEmpty(data.insurerNumber)) {
      await saveOtherUserSetting({
        userId,
        settingKey: 'insurer_number',
        body: {
          value: data.insurerNumber ?? '',
        },
      })
        .unwrap()
        .catch(handleApiError);
    }
  };

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await inviteUser({
      inviteUserRequestBody: {
        branchIds: data.branchIds,
        defaultBranchId: data.defaultBranchId,
        email: data.email as string,
        firstName: data.firstName,
        lastName: data.lastName,
        roles: data.roles,
      },
    })
      .unwrap()
      .then(updateOtherSettings(data))
      .then(() => {
        showNotification.success(i18n.t('general.notifications.invitationSent'));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.userManagement);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      isCreating
      header={{
        title: i18n.t('page.settings.actions.inviteUser'),
        breadcrumbs: [
          {label: i18n.t('entity.user.labels.users'), href: settingsRoutes.userManagement},
        ],
      }}
      data-testid={testIds.settings.userManagementInviteUser('UserDetail')}
    >
      <SettingsSection>
        <Form<FormValues>
          onSubmit={handleSubmit}
          experimentalZodSchema={getSchema('invite')}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <>
              <UserInfo
                data-testid={testIds.settings.userManagementInviteUser('UserInfo')}
                control={control}
              />

              <Separator />

              <UserBranches
                data-testid={testIds.settings.userManagementInviteUser('UserBranches')}
                control={control}
                formApi={formApi}
              />

              <Separator />

              <UserRoles
                data-testid={testIds.settings.userManagementInviteUser('UserRoles')}
                control={control}
                formApi={formApi}
              />

              <Separator />

              <Grid columns={2}>
                <FormField
                  control={control}
                  type="text"
                  name="insurerNumber"
                  label={i18n.t('page.settings.labels.insurerNumber')}
                />

                <FormField
                  control={control}
                  type="text"
                  name="customId"
                  label={i18n.t('page.settings.labels.customUserId')}
                />
              </Grid>

              <Space vertical={20} />

              <SettingsFooter
                actions={[
                  {
                    variant: 'secondary',
                    type: 'button',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.userManagement),
                    title: i18n.t('general.actions.cancel'),
                    'data-testid': testIds.settings.userManagementInviteUser('cancel'),
                  },
                  {
                    type: 'form-button',
                    title: i18n.t('general.actions.invite'),
                    'data-testid': testIds.settings.userManagementInviteUser('submit'),
                    control,
                    buttonType: 'submit',
                  },
                ]}
              />
            </>
          )}
        </Form>
      </SettingsSection>
    </SettingsTemplate>
  );
}
